<template>
  <div
    class="lpu-prices-service rounded-20"
    :class="[
      isMobile ? 'pa-4' : 'px-4 py-3',
      { 'bg-gray-40': isStriped },
    ]"
    data-qa="lpu_services_service"
  >
    <div
      class="d-flex"
      :class="isMobile
        ? 'flex-column ga-3'
        : 'align-center ga-8'"
    >
      <div
        class="lpu-prices-service__name text-body-1 min-w-0"
        :class="{ 'flex-grow-1': !isMobile }"
        data-qa="lpu_services_service_name_text"
      >
        {{ name }}
        <div
          v-if="durationLabel"
          class="text-body-2 text-secondary--text"
          data-qa="lpu_services_service_duration"
        >
          {{ durationLabel }}
        </div>
      </div>

      <div
        class="d-flex align-center flex-shrink-0"
        :class="isMobile ? 'justify-space-between w-100' : 'ga-8'"
      >
        <div
          class="text-subtitle-1 text--text flex-shrink-0"
          :class="{ 'text-right': !isMobile }"
          data-qa="lpu_services_service_price_full_text"
        >
          {{ priceLabel }}
        </div>

        <div
          v-if="showActionsColumn"
          class="lpu-prices-service__actions flex-shrink-0"
        >
          <v-btn
            depressed
            color="primary"
            :height="isMobile ? 48 : 52"
            :loading="isLoading"
            :tabindex="showAppointmentButton ? undefined : -1"
            :aria-hidden="!showAppointmentButton"
            class="branding-btn branding-btn--primary rounded-20 text-none text-subtitle-1"
            :class="{ 'lpu-prices-service__btn-ghost': !showAppointmentButton }"
            :data-qa="showAppointmentButton
              ? 'lpu_services_service_price_full_button'
              : undefined"
            @click.stop="onAppointmentClick"
          >
            {{ tStatic('Запись онлайн') }}
          </v-btn>
          <v-btn
            v-if="showToggle"
            depressed
            width="40"
            height="32"
            min-width="40"
            class="branding-btn branding-btn--background lpu-prices-service__toggle-btn"
            data-qa="lpu_services_service_price_toggle_calendar"
            @click.stop="emit('close-calendar')"
          >
            <span class="ui-icon-arrow-up" />
          </v-btn>
        </div>
      </div>
    </div>

    <div
      v-if="isCalendarOpen"
      class="lpu-prices-service__calendar d-flex flex-column ga-3 mt-3 mx-n3"
    >
      <div
        v-if="!isMobile"
        class="lpu-prices-service__divider"
      />
      <PricesServiceCalendar
        :day-items="dayItems"
        :active-date="activeDate || undefined"
        :active-day-slots="activeDaySlots"
        @select-day="selectDay"
        @slot="onCalendarSlot"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import useIsMobile from 'src/composables/useIsMobile'
import type { CommonContextStore } from 'src/types/context'
import useContextStore from 'src/store/useContextStore'
import formatPrice from 'src/functions/formatPrice'
import tDynamic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tDynamic'
import tStatic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tStatic'
import formatServiceDuration from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuPrices/functions/formatServiceDuration'
import useServiceRowSchedule from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuPrices/composables/useServiceRowSchedule'
import PricesServiceCalendar from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuPrices/components/PricesServiceCalendar.vue'
import type { SynonymPrice } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuPrices/types'

const props = defineProps<{
  service: SynonymPrice
  isStriped: boolean
  isCalendarOpen: boolean
  lpuId: number
  townTimedelta: number
}>()

type Emits = {
  (_event: 'open-calendar', _service: SynonymPrice): void
  (_event: 'close-calendar'): void
  (_event: 'empty-schedule', _service: SynonymPrice): void
  (_event: 'slot', _service: SynonymPrice, _date: string, _time: string): void
}

const emit = defineEmits<Emits>()
const isMobile = useIsMobile()
const { context }: CommonContextStore = useContextStore()
const {
  dayItems,
  activeDate,
  activeDaySlots,
  isLoading,
  selectDay,
  loadSlots,
} = useServiceRowSchedule({
  lpuId: props.lpuId,
  townTimedelta: props.townTimedelta,
  serviceId: props.service.synonym_id,
})
const currency = computed(() => context.currency.symbol)
const name = computed(() => tDynamic(props.service, 'name') || props.service.name)
const durationLabel = computed(() => formatServiceDuration(props.service.duration))
const priceLabel = computed(() => formatPrice({
  price: props.service.price,
  currencySymbol: currency.value,
}))
const canBook = computed(() => (
  Boolean(props.service.has_intervals)
  && Boolean(props.service.is_available_appointment_direct)
  && Boolean(props.service.appointment_on)
))
const showAppointmentButton = computed(() => canBook.value && !props.isCalendarOpen)
const showToggle = computed(() => canBook.value && props.isCalendarOpen)
const showActionsColumn = computed(() => !isMobile.value || canBook.value)

async function onAppointmentClick() {
  if (!showAppointmentButton.value || isLoading.value) {
    return
  }

  const result = await loadSlots()

  if (result === 'slots') {
    emit('open-calendar', props.service)

    return
  }

  if (result === 'empty') {
    emit('empty-schedule', props.service)
  }
}

function onCalendarSlot(time: string) {
  if (!activeDate.value) {
    return
  }

  emit('slot', props.service, activeDate.value, time)
}
</script>

<style scoped lang="scss">
@import 'src/styles/variables';

.lpu-prices-service {
  $toggle-btn-height: 32px;

  min-width: 0;
  max-width: 100%;

  &__name {
    word-break: break-word;
  }

  &__calendar {
    min-width: 0;
    max-width: 100%;

    // Место под стрелки DayTabs (left/right: -16px) и legend «Сегодня».
    padding: 8px 16px 0;
  }

  &__actions {
    position: relative;
    display: flex;
    align-items: center;
  }

  &__btn-ghost {
    visibility: hidden;
    pointer-events: none;
  }

  &__toggle-btn {
    position: absolute;
    left: 0;
    top: calc(50% - #{$toggle-btn-height / 2});
    border-radius: $border-radius-md;
  }

  &__divider {
    height: 1px;
    background-color: $ui-kit-bg-gray-60;
  }
}
</style>
